import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Heart, Trophy, ArrowRight, Sparkles } from 'lucide-react';
import { api } from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import GiveVoteModal from '@/components/GiveVoteModal';
import { useAuth } from '@/context/AuthContext';

const prettyMonth = (mk) => {
  if (!mk) return '';
  try {
    const d = new Date(`${mk}-01T00:00:00`);
    return d.toLocaleDateString(undefined, { month: 'long', year: 'numeric' });
  } catch {
    return mk;
  }
};

/**
 * Dashboard widget: shows daily votes remaining, a Give Vote button and — if the
 * Owner has revealed last month's top recipients — a small celebratory banner.
 * Only mounted for internal users (Clients are excluded upstream in DashboardPage).
 */
export default function KudosWidget() {
  const { user } = useAuth();
  const [status, setStatus] = useState(null);
  const [reveal, setReveal] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);

  const loadStatus = () => {
    api.get('/votes/my-status').then((r) => setStatus(r.data)).catch(() => setStatus(null));
  };

  useEffect(() => {
    loadStatus();
    api.get('/votes/reveal/latest').then((r) => setReveal(r.data?.reveal || null)).catch(() => setReveal(null));
  }, []);

  const left = status?.votes_remaining_today ?? 3;
  const limit = status?.daily_limit ?? 3;
  const givenToday = status?.votes_given_today ?? 0;
  const receivedMonth = status?.votes_received_this_month ?? 0;

  return (
    <>
      <Card className="shine-card p-5 rounded-xl mb-4 relative overflow-hidden" data-testid="kudos-widget">
        <div className="absolute -top-6 -right-6 h-28 w-28 rounded-full bg-amber-500/10 blur-2xl pointer-events-none" />
        <div className="flex flex-col md:flex-row md:items-center gap-4 relative">
          <div className="flex-shrink-0 h-14 w-14 rounded-xl bg-amber-500/15 border border-amber-500/35 flex items-center justify-center">
            <Heart className="h-6 w-6 text-amber-300" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-wider text-amber-300 font-bold">Peer recognition</div>
            <h3 className="text-lg sm:text-xl font-extrabold text-white leading-tight mt-0.5">
              You have <span className="text-amber-300">{left}</span> of {limit} votes left today
            </h3>
            <div className="text-xs text-slate-400 mt-1">
              Given today: <span className="text-white font-semibold">{givenToday}</span> · Received this month: <span className="text-white font-semibold">{receivedMonth}</span>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <Link to="/kudos" className="hidden sm:inline-flex text-xs text-amber-300 hover:text-amber-200 items-center gap-1">
              My kudos <ArrowRight className="h-3.5 w-3.5" />
            </Link>
            <Button
              onClick={() => setModalOpen(true)}
              disabled={left <= 0}
              data-testid="kudos-give-btn"
              className="bg-amber-500 hover:bg-amber-400 text-[#0a1628] font-semibold disabled:opacity-50"
            >
              <Sparkles className="h-4 w-4 mr-1.5" />
              {left <= 0 ? 'All 3 used today' : 'Give a vote'}
            </Button>
          </div>
        </div>
      </Card>

      {reveal && Array.isArray(reveal.top_recipients) && reveal.top_recipients.length > 0 && (
        <Card className="shine-card p-4 rounded-xl mb-4 border-amber-500/40" data-testid="kudos-reveal-banner">
          <div className="flex items-center gap-3">
            <div className="h-9 w-9 rounded-lg bg-amber-400/15 border border-amber-400/40 flex items-center justify-center text-xl flex-shrink-0">
              🏆
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[10px] uppercase tracking-wider text-amber-300 font-bold flex items-center gap-1">
                <Trophy className="h-3 w-3" /> {prettyMonth(reveal.month_key)} — Peer recognition
              </div>
              <div className="text-sm text-white font-semibold mt-0.5 truncate">
                {reveal.top_recipients.slice(0, 3).map((r, i) => (
                  <span key={r.user_id}>
                    {i > 0 ? ' · ' : ''}
                    <span className="text-amber-200">{['🥇', '🥈', '🥉'][i] || '⭐'} {r.name}</span>
                    <span className="text-slate-400 font-normal text-xs"> ({r.vote_count})</span>
                  </span>
                ))}
              </div>
            </div>
          </div>
        </Card>
      )}

      <GiveVoteModal
        open={modalOpen}
        onOpenChange={setModalOpen}
        currentUserId={user?.id}
        onGiven={() => loadStatus()}
      />
    </>
  );
}
